/* istanbul ignore file */
import { useEffect, useState } from 'react'
import useScript from 'react-script-hook'
import { error as logError } from '@divvy-web/logger'
import { createPlaid } from './factory'

const PLAID_LINK_STABLE_URL = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js'

const noop = () => {}

/*************************************************************************
* This is taken from https://github.com/plaid/react-plaid-link. There are
* too many problems with official plaid react component
* (see https://github.com/plaid/react-plaid-link/issues)
* so we have our own implementation here where we tweak it for our specific
* use and fix the issue we've had.
*************************************************************************/

/**
 * This hook loads Plaid script and manages the Plaid Link creation for you.
 * You get easy open & exit methods to call and loading & error states.
 *
 * This will destroy the Plaid UI on un-mounting so it's up to you to be
 * graceful to the user.
 *
 * A new Plaid instance is created every time the individual options change.
 * It's up to you to prevent unnecessary re-creations on re-render.
 */
export const usePlaidLink = (options) => {
  // Asynchronously load the plaid/link/stable url into the DOM
  const [loading, error] = useScript({ src: PLAID_LINK_STABLE_URL })

  // internal state
  const [plaid, setPlaid] = useState(null)
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const [retries, setRetries] = useState(0)

  useEffect(() => {
    // If the link.js script is still loading, return prematurely
    // also, if we pass an empty options config
    if (loading || !options.clientName) {
      return
    }

    if (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading Plaid', error)
      return
    }

    if (!window.Plaid) {
      const eventName = 'accounting-integrations-plaid-not-set'
      const message = 'window.Plaid is not set'
      try {
        logError({
          channel: 'accounting-integrations-plaid-error',
          error: message,
          eventName,
          squad: 'accounting-integrations',
          squadCode: 'ACC'
        })
      } catch (err) {
        console.error('[WzAwM10=] Unable to initialize New Relic')
      }

      // I don't know why but even after `loading` is false, sometimes in production `window.Plaid` is not set
      // yet, so we retry every 50ms for 5 seconds
      if (retries < 100) {
        // eslint-disable-next-line no-console
        console.error('retrying in a 50ms to see if window.Plaid loads')
        setTimeout(() => {
          setRetries(retries + 1)
        }, 50)
      }
      return
    }

    // if an old plaid instance exists, destroy it before
    // creating a new one
    if (plaid != null) {
      plaid.exit({ force: true }, () => plaid.destroy())
    }

    const next = createPlaid({
      ...options,
      onLoad: () => {
        setIframeLoaded(true)
        options.onLoad && options.onLoad()
      }
    })

    setPlaid(next)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, options.token, retries])

  return {
    error,
    exit: plaid ? plaid.exit : noop,
    open: plaid ? plaid.open : noop,
    ready: !loading || iframeLoaded
  }
}
